// src/polaris-components/BrandBanner.tsx
import React from "react";
import ProfileAvatar from "./ProfileAvatar";
import {isAthlete, isStaff, isAdmin, isCoach} from "../utils/auth";
import "../styles/brand-banner.css";

// Keep these imports so bundlers resolve the assets
import defaultLogo from "../images/Polaris Logo.svg";
import defaultAvatar from "../images/banner/avatar-image.png"; // Regular avatar fallback
import starIcon from "../images/banner/Polaris-advising star-white.svg"; // Star icon for first-time users

export type BrandBannerProps = {
    /** Polaris logo (centered at top) */
    logoSrc?: string;
    /** DEPRECATED: Use ProfileAvatar without parameters. User avatar shown on the left of text block */
    avatarSrc?: string;
    /** User display name (big text) */
    displayName?: string;
    /** User title (first item in the subtitle row) */
    title?: string;
    /** Company name (right of the pipe in the subtitle row) */
    companyName?: string;
    /** Whether user has a profile created */
    hasProfile?: boolean;
    /** Profile image URL from API (S3 URL) */
    profileImageUrl?: string;
    /** Override the subtitle directly (bypasses role-based logic) */
    subtitleOverride?: string;

    /** Visual props preserved */
    bg?: string;
    side?: "left" | "right";
    offset?: number;
    className?: string;
};

const BrandBanner: React.FC<BrandBannerProps> = ({
                                                     logoSrc: _logoSrc = defaultLogo,
                                                     avatarSrc,  // Deprecated - kept for backwards compatibility
                                                     displayName = "Guest",
                                                     title = "",
                                                     companyName = "",
                                                     hasProfile = false,
                                                     profileImageUrl,
                                                     subtitleOverride,
                                                     bg = "#000", // black by default
                                                     side = "left",
                                                     offset = 16,
                                                     className = "",
                                                 }) => {
    const titleId = React.useId();

    // Determine subtitle based on user role
    // Athletes: show graduation year (from title prop)
    // Staff/Admin: show company name
    // subtitleOverride takes priority (used for public shared profiles without auth context)
    let subtitle = "";
    if (subtitleOverride !== undefined) {
        subtitle = subtitleOverride;
    } else if (isAthlete()) {
        subtitle = title?.trim() || "";
    } else if (isStaff() || isAdmin() || isCoach()) {
        subtitle = companyName?.trim() || "";
    }

    // If first-time user, show welcome view with star icon
    if (!hasProfile) {
        return (
            <section
                className={[
                    "brand-banner",
                    "brand-banner--fixed",
                    side === "right" ? "brand-banner--right" : "brand-banner--left",
                    className,
                ].join(" ")}
                style={{ ["--bb-bg" as any]: bg, ["--bb-offset" as any]: `${offset}px` }}
                aria-labelledby={titleId}
            >
                <div className="bb-inner">
                    {/* Top logo (centered) */}
                    <div className="bb-top">
                        <img className="bb-logo" src={defaultLogo} alt="Polaris Athlete" />
                    </div>

                    {/* Avatar + identity */}
                    <div className="bb-row">
                        {/* Show star icon for first-time users */}
                        <div className="bb-avatar bb-avatar--star" aria-hidden="true" data-testid="first-login-icon">
                            <img src={starIcon} alt="" className="bb-star-icon" />
                        </div>
                        <div className="bb-id">
                            <h3 id={titleId} className="bb-title">{displayName}</h3>
                            <p className="bb-subtitle">{subtitle || "\u200b"}</p>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    // Default view for returning users
    return (
        <section
            className={[
                "brand-banner",
                "brand-banner--fixed",
                side === "right" ? "brand-banner--right" : "brand-banner--left",
                className,
            ].join(" ")}
            style={{ ["--bb-bg" as any]: bg, ["--bb-offset" as any]: `${offset}px` }}
            aria-labelledby={titleId}
        >
            <div className="bb-inner">
                {/* Top logo (centered) */}
                <div className="bb-top">
                    <img className="bb-logo" src={defaultLogo} alt="Polaris Athlete" />
                </div>

                {/* Avatar + identity */}
                <div className="bb-row">
                    {/* Always use ProfileAvatar when user has profile - uses imageUrl from API, no fetching needed */}
                    {hasProfile ? (
                        <ProfileAvatar
                            displayName={displayName}
                            className="bb-avatar-img"
                            size="medium"
                            alt="Profile Picture"
                            allowUpload={false}
                            imageUrl={profileImageUrl}
                            skipAvatarFetch={true}
                        />
                    ) : (
                        <img
                            className="bb-avatar"
                            src={avatarSrc || defaultAvatar}
                            alt=""
                            aria-hidden="true"
                        />
                    )}
                    <div className="bb-id">
                        <h3 id={titleId} className="bb-title">{displayName}</h3>
                        <p className="bb-subtitle">{subtitle || "\u200b"}</p>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default BrandBanner;
